import { Exclude, Expose, plainToClass, Type } from "class-transformer";
import * as t from "io-ts";
import { GeoCoord, GeoCoordValidator } from "./geo-coord";
import { ShiftEntity, ShiftValidator } from "./shift";
import { GeofenceEventEntity, GeofenceEventValidator } from "./geofence-event";
import { DispatchArrayValidator, DispatchEntity } from "./dispatch";
import { Default } from "../util/decorators";
import { differenceInMinutes, isAfter, subMinutes } from "../date/date";

export const PersonStateValidator = t.intersection([
  t.type({
    inShift: t.boolean,
    onJob: t.boolean,
    onSite: t.boolean,
  }),
  t.partial({
    location: GeoCoordValidator,
    shift: ShiftValidator,
    geofenceEvent: GeofenceEventValidator,
    dispatches: DispatchArrayValidator,
    lastSyncDttm: t.number,
  }),
]);

export type PersonState = t.TypeOf<typeof PersonStateValidator>;

/**
 * State of a person.
 */
@Exclude()
export class PersonStateEntity {
  @Expose()
  location?: GeoCoord;

  @Expose()
  @Type(() => ShiftEntity)
  shift?: ShiftEntity;

  @Expose()
  @Type(() => GeofenceEventEntity)
  geofenceEvent?: GeofenceEventEntity;

  @Expose()
  @Type(() => DispatchEntity)
  dispatches?: DispatchEntity[];

  @Expose()
  lastSyncDttm?: number;

  @Expose()
  @Default(false)
  inShift = false;

  @Expose()
  @Default(false)
  onJob = false;

  @Expose()
  @Default(false)
  onSite = false;

  /**
   * Constructor for PersonStateEntity. Recommend using the {@link create} method instead.
   * @hideconstructor
   */
  constructor() {
    // Empty constructor for 'class-transformer'
  }

  /**
   * Create a PersonStateEntity from a PersonState type value.
   *
   * @param personState
   */
  static from(personState: PersonState): PersonStateEntity {
    return plainToClass(PersonStateEntity, personState, {
      excludeExtraneousValues: true,
    });
  }

  get stationary(): boolean {
    if (this.inShift && this.lastSyncDttm && this.location?.dttm) {
      const lastSyncDate = new Date(this.lastSyncDttm);
      const locationDate = new Date(this.location.dttm);

      const currDate = new Date();
      if (isAfter(lastSyncDate, subMinutes(currDate, 15))) {
        if (differenceInMinutes(currDate, locationDate) >= 90) {
          return true;
        }
      }
    }

    return false;
  }
}
